/**
 * Recovery context reducer with guarded state transitions
 */

import {
  ActionType,
  RecoveryStep,
  initialState,
  isValidTransition,
  computePercent,
} from './types';
import logger from '../../utils/logger';

/**
 * Recovery reducer with guarded transitions
 * @param {Object} state - Current state
 * @param {Object} action - Action to process
 * @returns {Object} New state
 */
export function recoveryReducer(state = initialState, action) {
  switch (action.type) {
    case ActionType.INIT: {
      const { hasBackup, needsRecovery, backupInfo, hasSSSS, needsBackupSetup, error } = action.payload;
      // If there's an error (e.g., timeout), don't change step - stay in current state
      // and preserve null values to indicate "unknown" state
      const newStep = error ? state.step : (needsRecovery ? RecoveryStep.AWAIT_KEY : RecoveryStep.IDLE);
      logger.info('RECOVERY: [Reducer] === INIT Action ===', {
        hasBackup,
        needsRecovery,
        hasSSSS,
        needsBackupSetup,
        error: error?.code,
        newStep,
        previousStep: state.step,
      });

      // When error is present, preserve null values to indicate "unknown" state
      // This prevents ChatBackupPage from auto-bootstrapping when server state is unknown
      return {
        ...state,
        hasBackup: error ? null : Boolean(hasBackup),
        hasSSSS: error ? null : Boolean(hasSSSS),
        needsRecovery: error ? false : Boolean(needsRecovery),
        needsBackupSetup: error ? false : Boolean(needsBackupSetup),
        step: newStep,
        error: error || null,
        progress: { imported: 0, total: 0, percent: 0 },
      };
    }

    case ActionType.OPEN_MODAL: {
      if (!state.needsRecovery) {
        logger.warn('[RecoveryReducer] Cannot open modal when recovery not needed');
        return state;
      }

      // Only allow opening modal from IDLE or ERROR states
      // Not during active recovery operations
      const allowedSteps = [RecoveryStep.IDLE, RecoveryStep.ERROR];
      if (!allowedSteps.includes(state.step)) {
        logger.warn('[RecoveryReducer] Cannot open modal from step:', state.step);
        return state;
      }

      return {
        ...state,
        step: RecoveryStep.AWAIT_KEY,
        // Only set startedAt if not already set and a timestamp is provided
        startedAt: state.startedAt ?? action.payload?.timestamp ?? state.startedAt,
        error: null,
      };
    }

    case ActionType.SUBMIT_KEY: {
      // Note: The key itself is NOT stored in state
      // It's managed separately in memory by the provider
      if (!isValidTransition(state.step, RecoveryStep.VALIDATING)) {
        logger.warn('[RecoveryReducer] Cannot submit key from step:', state.step);
        return state;
      }

      return {
        ...state,
        step: RecoveryStep.VALIDATING,
        error: null,
      };
    }

    case ActionType.SET_STEP: {
      const { step: newStep, timestamp } = action.payload || {};

      if (!isValidTransition(state.step, newStep)) {
        logger.warn('[RecoveryReducer] Invalid transition from', state.step, 'to', newStep);
        return state;
      }

      return {
        ...state,
        step: newStep,
        // Deterministically set startedAt only when transitioning to RESTORING
        // and a timestamp is provided, without overwriting an existing value
        startedAt: newStep === RecoveryStep.RESTORING && !state.startedAt
          ? (timestamp ?? state.startedAt)
          : state.startedAt,
      };
    }

    case ActionType.RESTORE: {
      // Restore action is handled by provider logic
      // This just ensures we're in the right step
      if (state.step !== RecoveryStep.VALIDATING && state.step !== RecoveryStep.RESTORING) {
        logger.warn('[RecoveryReducer] Cannot start restore from step:', state.step);
        return state;
      }

      return {
        ...state,
        step: RecoveryStep.RESTORING,
      };
    }

    case ActionType.SSSS: {
      if (state.step !== RecoveryStep.RESTORING) {
        logger.warn('[RecoveryReducer] Cannot start SSSS from step:', state.step);
        return state;
      }

      return {
        ...state,
        step: RecoveryStep.SSSS,
        error: null,
      };
    }

    case ActionType.VERIFY_DEVICE: {
      if (state.step !== RecoveryStep.SSSS) {
        logger.warn('[RecoveryReducer] Cannot verify device from step:', state.step);
        return state;
      }

      return {
        ...state,
        step: RecoveryStep.VERIFYING,
        error: null,
      };
    }

    case ActionType.COMPLETE: {
      if (state.step !== RecoveryStep.VERIFYING) {
        logger.warn('RECOVERY: [Reducer] Cannot complete from step:', state.step);
        return state;
      }

      logger.debug('RECOVERY: [Reducer] COMPLETE action - setting needsRecovery to false');
      return {
        ...state,
        step: RecoveryStep.DONE,
        needsRecovery: false,
        error: null,
        progress: { imported: 0, total: 0, percent: 100 },
        startedAt: null,
      };
    }

    case ActionType.FAIL: {
      const { code, message } = action.payload;
      logger.error('[RecoveryReducer] Recovery failed:', code, message);

      return {
        ...state,
        step: RecoveryStep.ERROR,
        error: { code, message },
      };
    }

    case ActionType.RESET: {
      logger.debug('RECOVERY: [Reducer] Resetting recovery state');

      return {
        ...state,
        step: RecoveryStep.IDLE,
        error: null,
        progress: { imported: 0, total: 0, percent: 0 },
        startedAt: null,
      };
    }

    case ActionType.SKIP: {
      logger.debug('[RecoveryReducer] Skipping recovery');

      // Skip moves to idle but preserves needsRecovery status
      // UI decides whether to gate features
      return {
        ...state,
        step: RecoveryStep.IDLE,
        error: null,
        progress: { imported: 0, total: 0, percent: 0 },
        startedAt: null,
        // needsRecovery is intentionally NOT changed
      };
    }

    case ActionType.SET_PROGRESS: {
      const { imported, total } = action.payload;
      const percent = computePercent(imported, total);

      return {
        ...state,
        progress: { imported, total, percent },
      };
    }

    case ActionType.SET_STARTED_AT: {
      return {
        ...state,
        startedAt: action.payload,
      };
    }

    case ActionType.CLEAR_ERROR: {
      return {
        ...state,
        error: null,
      };
    }

    default:
      return state;
  }
}
